import React, { useState } from "react";
import {
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Button,
    Typography,
    useMediaQuery,
    DialogTitle,
    Dialog,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
    Box
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useForm, Controller } from "react-hook-form";
import { useAtom } from "jotai";
import { useTheme } from "@mui/material/styles";
import { mbxUserAtom } from "../../atoms/atoms";
import {
    openAddWebhookModalAtom,
    openViewWebhookModalAtom,
    selectedWebhookAtom,
    WEBHOOK_EVENT_TYPES,
    WEBHOOK_EVENT_TYPE_LABELS
} from "../../atoms/webhooks";
import { postApiWithStatusCode } from "../../modules/AmplifyServices";
import { userPageSnackbarAtom } from "../../atoms/usersTable";

const AddWebhookModal = ({ refetch }) => {
    const theme = useTheme();
    const [openAddWebhookModal, setOpenAddWebhookModal] = useAtom(
        openAddWebhookModalAtom
    );
    const [, setOpenViewWebhookModal] = useAtom(openViewWebhookModalAtom);
    const [, setSelectedWebhook] = useAtom(selectedWebhookAtom);
    const [mbxUser] = useAtom(mbxUserAtom);
    const [, setWebhookPageSnackbar] = useAtom(userPageSnackbarAtom);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [headers, setHeaders] = useState([]);

    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });

    const form = useForm({
        defaultValues: {
            name: "",
            description: "",
            eventType: "",
            url: ""
        },
        mode: "onSubmit",
        reValidateMode: "onSubmit"
    });

    const {
        register,
        handleSubmit,
        control,
        reset,
        formState: { errors }
    } = form;

    const handleClose = () => {
        reset();
        setHeaders([]);
        setOpenAddWebhookModal(false);
    };

    const handleAddHeader = () => {
        setHeaders([...headers, { key: "", value: "" }]);
    };

    const handleRemoveHeader = (index) => {
        const newHeaders = headers.filter((_, i) => i !== index);
        setHeaders(newHeaders);
    };

    const handleHeaderChange = (index, field, value) => {
        const newHeaders = [...headers];
        newHeaders[index][field] = value;
        setHeaders(newHeaders);
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        try {
            const headersObj = headers.reduce((acc, { key, value }) => {
                if (key && value) {
                    acc[key] = value;
                }
                return acc;
            }, {});

            const webhookData = {
                companyId: mbxUser.companyId,
                name: data.name,
                description: data.description || undefined,
                eventType: data.eventType,
                url: data.url,
                method: "POST",
                headers:
                    Object.keys(headersObj).length > 0 ? headersObj : undefined,
                status: "inactive"
            };

            const { body, statusCode } = await postApiWithStatusCode(
                "MB-rest-api",
                "/v1/mb/webhooks",
                {
                    body: webhookData
                }
            );

            if (statusCode >= 200 && statusCode < 300) {
                setWebhookPageSnackbar({
                    open: true,
                    message: "Webhook created successfully. Publish it when you're ready to go live.",
                    severity: "success"
                });
                handleClose();
                if (refetch) refetch();

                // Open view modal to show the created webhook details
                if (body?.webhook) {
                    setSelectedWebhook(body.webhook);
                    setOpenViewWebhookModal(true);
                }
            } else {
                throw new Error(body?.message || "Failed to create webhook");
            }
        } catch (error) {
            console.error("Error creating webhook:", error);
            setWebhookPageSnackbar({
                open: true,
                message: error.message || "Failed to create webhook",
                severity: "error"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog
            open={openAddWebhookModal}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle sx={{ paddingBottom: "16px" }}>
                Create Webhook
            </DialogTitle>
            <form
                noValidate
                onSubmit={handleSubmit(onSubmit)}
                style={{
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    height: "100%"
                }}
            >
                <DialogContent sx={{ minHeight: "200px" }}>
                    <Grid
                        container
                        spacing={2.25}
                        justifyContent={!isDesktop && "center"}
                        alignItems="flex-start"
                    >
                        <Grid item xs={12}>
                            <TextField
                                id="name"
                                name="name"
                                type="text"
                                size="small"
                                label="Name"
                                disabled={isSubmitting}
                                autoFocus
                                fullWidth
                                {...register("name", {
                                    required: "Please enter a name"
                                })}
                                error={!!errors.name}
                                helperText={errors.name?.message}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                id="description"
                                name="description"
                                type="text"
                                size="small"
                                label="Description (optional)"
                                disabled={isSubmitting}
                                fullWidth
                                multiline
                                rows={2}
                                {...register("description")}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="eventType-label">
                                    Event Type
                                </InputLabel>
                                <Controller
                                    name="eventType"
                                    control={control}
                                    rules={{
                                        required: "Please select an event type"
                                    }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            labelId="eventType-label"
                                            label="Event Type"
                                            disabled={isSubmitting}
                                            error={!!errors.eventType}
                                        >
                                            {Object.entries(
                                                WEBHOOK_EVENT_TYPES
                                            ).map(([key, value]) => (
                                                <MenuItem
                                                    key={key}
                                                    value={value}
                                                >
                                                    {
                                                        WEBHOOK_EVENT_TYPE_LABELS[
                                                            value
                                                        ]
                                                    }{" "}
                                                    ({value})
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    )}
                                />
                                {errors.eventType && (
                                    <Typography
                                        variant="caption"
                                        color="error"
                                        sx={{ mt: 0.5, ml: 2 }}
                                    >
                                        {errors.eventType.message}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                id="url"
                                name="url"
                                type="url"
                                size="small"
                                label="Webhook URL"
                                disabled={isSubmitting}
                                fullWidth
                                placeholder="https://example.com/webhook"
                                {...register("url", {
                                    required: "Please enter a URL",
                                    pattern: {
                                        value: /^https?:\/\/.+/,
                                        message:
                                            "Please enter a valid URL starting with http:// or https://"
                                    }
                                })}
                                error={!!errors.url}
                                helperText={errors.url?.message}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mb: 1
                                }}
                            >
                                <Typography
                                    variant="subtitle2"
                                    color="text.secondary"
                                >
                                    Headers (optional)
                                </Typography>
                                <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddHeader}
                                    disabled={isSubmitting}
                                >
                                    Add Header
                                </Button>
                            </Box>
                            {headers.map((header, index) => (
                                <Grid
                                    container
                                    spacing={1}
                                    key={index}
                                    sx={{ mb: 1 }}
                                >
                                    <Grid item xs={5.5}>
                                        <TextField
                                            size="small"
                                            placeholder="Header Key"
                                            value={header.key}
                                            onChange={(e) =>
                                                handleHeaderChange(
                                                    index,
                                                    "key",
                                                    e.target.value
                                                )
                                            }
                                            disabled={isSubmitting}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={5.5}>
                                        <TextField
                                            size="small"
                                            placeholder="Header Value"
                                            value={header.value}
                                            onChange={(e) =>
                                                handleHeaderChange(
                                                    index,
                                                    "value",
                                                    e.target.value
                                                )
                                            }
                                            disabled={isSubmitting}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={0.5}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center"
                                        }}
                                    >
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                handleRemoveHeader(index)
                                            }
                                            disabled={isSubmitting}
                                            color="info"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions
                    sx={{
                        display: "flex",
                        justifyContent: "end",
                        px: 3,
                        pb: 2
                    }}
                >
                    <Grid display={"flex"} gap={"18px"} width={"auto"}>
                        {isDesktop && (
                            <Button
                                onClick={handleClose}
                                variant="outlined"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                        )}
                        <LoadingButton
                            color="primary"
                            variant="contained"
                            loading={isSubmitting}
                            type="submit"
                            sx={{ minWidth: "100px" }}
                        >
                            Create
                        </LoadingButton>
                    </Grid>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddWebhookModal;
